import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import type { Account } from '../../../hooks/useAccounts';

export type StatementFilter = {
  dateFrom: string;
  dateTo: string;
  type: 'all' | 'receita' | 'despesa' | 'transferencia';
  status: 'all' | 'pago' | 'recebido' | 'pendente' | 'vencido';
  search: string;
  paymentMethod: string | null; // null = todos
};

export type StatementTransaction = {
  id: string;
  description: string;
  category: string;
  subcategory: string | null;
  type: 'receita' | 'despesa';
  amount: number;
  /** Data de vencimento / competência */
  date: string;
  /** Data real de pagamento/recebimento (pode diferir de date) */
  paid_at: string | null;
  status: string;
  notes: string | null;
  transaction_subtype: string | null;
  payment_method_detail: string | null;
  recurrence: string | null;
  installments: number;
  installment_number: number;
  installment_group_id: string | null;
  runningBalance: number | null;
  affectsBalance: boolean;
};

export type StatementSummary = {
  totalReceived: number;
  totalPaid: number;
  totalPending: number;
  totalTransfers: number;
  countTotal: number;
  countPaid: number;
  countPending: number;
  net: number;
};

const getDefaultDates = () => {
  const now = new Date();
  const from = new Date(now.getFullYear(), now.getMonth(), 1);
  const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    dateFrom: from.toISOString().split('T')[0],
    dateTo: to.toISOString().split('T')[0],
  };
};

export const useAccountStatement = (accountId: string | undefined) => {
  const { user } = useAuth();
  const defaults = getDefaultDates();

  const [account, setAccount] = useState<Account | null>(null);
  const [transactions, setTransactions] = useState<StatementTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [accountLoading, setAccountLoading] = useState(true);
  const [summary, setSummary] = useState<StatementSummary>({
    totalReceived: 0, totalPaid: 0, totalPending: 0, totalTransfers: 0,
    countTotal: 0, countPaid: 0, countPending: 0, net: 0,
  });

  const [filters, setFilters] = useState<StatementFilter>({
    dateFrom: defaults.dateFrom,
    dateTo: defaults.dateTo,
    type: 'all',
    status: 'all',
    search: '',
    paymentMethod: null,
  });

  // Fetch account info
  useEffect(() => {
    if (!accountId || !user) return;
    setAccountLoading(true);
    supabase
      .from('accounts')
      .select('*')
      .eq('id', accountId)
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        setAccount(data as Account | null);
        setAccountLoading(false);
      });
  }, [accountId, user]);

  const fetchStatement = useCallback(async () => {
    if (!accountId || !user) return;
    setLoading(true);

    let query = supabase
      .from('transactions')
      .select('id, description, category, subcategory, type, amount, date, paid_at, status, notes, transaction_subtype, payment_method_detail, recurrence, installments, installment_number, installment_group_id')
      .eq('account_id', accountId)
      .eq('user_id', user.id)
      .is('deleted_at', null)
      .gte('date', filters.dateFrom)
      .lte('date', filters.dateTo)
      .order('date', { ascending: true })
      .order('created_at', { ascending: true });

    if (filters.type === 'transferencia') {
      query = query.eq('transaction_subtype', 'transferencia');
    } else if (filters.type !== 'all') {
      query = query.eq('type', filters.type).neq('transaction_subtype', 'transferencia');
    }

    if (filters.status !== 'all') {
      const statusMap: Record<string, string[]> = {
        pago: ['pago', 'recebido'],
        recebido: ['recebido'],
        pendente: ['pendente'],
        vencido: ['vencido'],
      };
      const statuses = statusMap[filters.status] ?? [filters.status];
      if (statuses.length === 1) {
        query = query.eq('status', statuses[0]);
      } else {
        query = query.in('status', statuses);
      }
    }

    const { data, error } = await query;
    if (error) { setLoading(false); return; }

    const raw = (data ?? []) as {
      id: string; description: string; category: string; subcategory: string | null;
      type: string; amount: number; date: string; paid_at: string | null; status: string; notes: string | null;
      transaction_subtype: string | null; payment_method_detail: string | null; recurrence: string | null;
      installments: number; installment_number: number; installment_group_id: string | null;
    }[];

    // Search filter (client-side)
    const searched = filters.search.trim()
      ? raw.filter(t =>
          t.description.toLowerCase().includes(filters.search.toLowerCase()) ||
          t.category.toLowerCase().includes(filters.search.toLowerCase())
        )
      : raw;

    // Payment method filter (client-side) — only applies to records that have the field
    const filtered = filters.paymentMethod
      ? searched.filter(t => t.payment_method_detail === filters.paymentMethod)
      : searched;

    // ── Calculate progressive balance ────────────────────────────────────────
    // Start from the account's initial_balance PLUS all paid transactions
    // BEFORE the dateFrom filter (carry-forward balance).
    let carryBalance = 0;
    if (account) {
      // Fetch all paid transactions before the period to get carry-forward saldo
      const { data: prevTxs } = await supabase
        .from('transactions')
        .select('type, amount, status, transaction_subtype')
        .eq('account_id', accountId)
        .eq('user_id', user.id)
        .is('deleted_at', null)
        .lt('date', filters.dateFrom)
        .in('status', ['pago', 'recebido']);

      const prevPaid = (prevTxs ?? []) as { type: string; amount: number; status: string; transaction_subtype: string | null }[];
      const prevIncome = prevPaid.filter(t => t.type === 'receita').reduce((s, t) => s + Number(t.amount), 0);
      const prevExpense = prevPaid.filter(t => t.type === 'despesa').reduce((s, t) => s + Number(t.amount), 0);
      carryBalance = (account.initial_balance ?? 0) + prevIncome - prevExpense;
    }

    let runningBalance = carryBalance;
    const enriched: StatementTransaction[] = filtered.map(t => {
      const isIncome = t.type === 'receita';
      const isPaid = t.status === 'pago' || t.status === 'recebido';
      const affectsBalance = isPaid;

      if (affectsBalance) {
        runningBalance = isIncome
          ? runningBalance + Number(t.amount)
          : runningBalance - Number(t.amount);
      }

      return {
        id: t.id,
        description: t.description,
        category: t.category,
        subcategory: t.subcategory,
        type: t.type as 'receita' | 'despesa',
        amount: Number(t.amount),
        date: t.date,
        paid_at: t.paid_at ?? null,
        status: t.status,
        notes: t.notes,
        transaction_subtype: t.transaction_subtype,
        payment_method_detail: t.payment_method_detail,
        recurrence: t.recurrence,
        installments: t.installments,
        installment_number: t.installment_number,
        installment_group_id: t.installment_group_id,
        runningBalance: affectsBalance ? runningBalance : null,
        affectsBalance,
      };
    });

    // ── Summary ──────────────────────────────────────────────────────────────
    const nonTransfer = filtered.filter(t => t.transaction_subtype !== 'transferencia');
    const transfers = filtered.filter(t => t.transaction_subtype === 'transferencia');
    const paid = nonTransfer.filter(t => t.status === 'pago' || t.status === 'recebido');
    const pending = nonTransfer.filter(t => t.status === 'pendente' || t.status === 'vencido');

    const totalReceived = nonTransfer.filter(t => t.type === 'receita' && (t.status === 'recebido' || t.status === 'pago')).reduce((s, t) => s + Number(t.amount), 0);
    const totalPaid = nonTransfer.filter(t => t.type === 'despesa' && t.status === 'pago').reduce((s, t) => s + Number(t.amount), 0);
    const totalPending = pending.reduce((s, t) => s + Number(t.amount), 0);
    const totalTransfers = transfers.reduce((s, t) => s + Number(t.amount), 0);

    setSummary({
      totalReceived, totalPaid, totalPending, totalTransfers,
      countTotal: searched.length,
      countPaid: paid.length,
      countPending: pending.length,
      net: totalReceived - totalPaid,
    });

    setTransactions(enriched.reverse()); // show newest first for display
    setLoading(false);
  }, [accountId, user, account, filters]);

  useEffect(() => {
    if (!accountLoading) fetchStatement();
  }, [fetchStatement, accountLoading]);

  const updateFilters = useCallback((partial: Partial<StatementFilter>) => {
    setFilters(prev => ({ ...prev, ...partial }));
  }, []);

  const setQuickPeriod = useCallback((period: 'thisMonth' | 'lastMonth' | 'last3' | 'last6' | 'thisYear' | 'all') => {
    const now = new Date();
    let from: Date, to: Date;
    switch (period) {
      case 'thisMonth':
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'lastMonth':
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        to = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'last3':
        from = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'last6':
        from = new Date(now.getFullYear(), now.getMonth() - 5, 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'thisYear':
        from = new Date(now.getFullYear(), 0, 1);
        to = new Date(now.getFullYear(), 11, 31);
        break;
      default: // all
        from = new Date(2000, 0, 1);
        to = new Date(now.getFullYear() + 1, 11, 31);
        break;
    }
    setFilters(prev => ({
      ...prev,
      dateFrom: from.toISOString().split('T')[0],
      dateTo: to.toISOString().split('T')[0],
    }));
  }, []);

  return {
    account, accountLoading,
    transactions, loading,
    summary, filters,
    updateFilters, setQuickPeriod,
    refetch: fetchStatement,
  };
};
